import { useState } from 'react';
import { RefreshCw, CheckCircle, AlertTriangle, Undo2 } from 'lucide-react';
import { Button, Badge, Modal, EmptyState } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import {
  usePayouts,
  useSyncPayouts,
  useConfirmPayout,
  useDisputePayout,
  useRevertPayout,
  useCompanies,
  useBankAccounts,
} from '../../../hooks/useFinancialOps';
import { formatCurrency, formatDateBR } from '../../../utils/format';

const STATUS_TONE: Record<string, 'neutral' | 'accent' | 'warning' | 'danger' | 'info'> = {
  PENDING: 'warning',
  CONFIRMED: 'accent',
  DISPUTED: 'danger',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pendente de vincular',
  CONFIRMED: 'Confirmado',
  DISPUTED: 'Contestado',
};

const DEFAULT_TAX_RATE_BY_REGIME: Record<string, number> = {
  SIMPLES_NACIONAL: 0.06,
  LUCRO_PRESUMIDO: 0.1133,
  LUCRO_REAL: 0,
  MEI: 0,
};

function getCompanyTaxRate(company: any): number {
  if (!company) {
    return 0;
  }
  if (company.defaultTaxRate !== null && company.defaultTaxRate !== undefined) {
    return Number(company.defaultTaxRate);
  }
  return DEFAULT_TAX_RATE_BY_REGIME[company.taxRegime] ?? 0;
}

export function PayoutsPage() {
  const [filterStatus, setFilterStatus] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');

  const params: Record<string, any> = {};
  if (filterStatus) {
    params.status = filterStatus;
  }
  if (filterStart) {
    params.startDate = filterStart;
  }
  if (filterEnd) {
    params.endDate = filterEnd;
  }

  const { data, isLoading } = usePayouts(params);
  const syncPayouts = useSyncPayouts();
  const confirmPayout = useConfirmPayout();
  const disputePayout = useDisputePayout();
  const revertPayout = useRevertPayout();

  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const [confirmModal, setConfirmModal] = useState(false);
  const [confirmingPayout, setConfirmingPayout] = useState<any>(null);
  const [confirmCompanyId, setConfirmCompanyId] = useState('');
  const [confirmAccountId, setConfirmAccountId] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');

  const { data: bankAccountsData } = useBankAccounts(confirmCompanyId);
  const bankAccounts = bankAccountsData?.data ?? [];

  const payouts = data?.data ?? [];

  function openConfirmModal(payout: any) {
    setConfirmingPayout(payout);
    setConfirmCompanyId('');
    setConfirmAccountId('');
    setConfirmNotes('');
    setConfirmModal(true);
  }

  async function handleConfirm() {
    if (!confirmCompanyId || !confirmAccountId || !confirmingPayout) {
      return;
    }
    await confirmPayout.mutateAsync({
      id: confirmingPayout.id,
      companyId: confirmCompanyId,
      bankAccountId: confirmAccountId,
      notes: confirmNotes || undefined,
    });
    setConfirmModal(false);
  }

  if (companies.length === 0) {
    return (
      <EmptyState
        title="Cadastre uma empresa primeiro"
        body="Para confirmar repasses Stripe, é necessário ter ao menos uma empresa cadastrada em Configuração → Financeiro → Empresas."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Status
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Todos</option>
            <option value="PENDING">Pendente</option>
            <option value="CONFIRMED">Confirmado</option>
            <option value="DISPUTED">Contestado</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            De
          </label>
          <input
            type="date"
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={filterStart}
            onChange={(e) => setFilterStart(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Até
          </label>
          <input
            type="date"
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={filterEnd}
            onChange={(e) => setFilterEnd(e.target.value)}
          />
        </div>
        <Button
          onClick={() => syncPayouts.mutate()}
          loading={syncPayouts.isPending}
          variant="secondary"
          size="sm"
        >
          <RefreshCw size={16} className="mr-1" /> Sincronizar
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : payouts.length === 0 ? (
        <EmptyState
          title="Nenhum repasse encontrado"
          body="Clique em Sincronizar para buscar repasses do Stripe."
        />
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-ink-secondary">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Data</th>
                <th className="px-4 py-2 text-right font-medium">Receita Bruta</th>
                <th className="px-4 py-2 text-right font-medium">Taxas Stripe</th>
                <th className="px-4 py-2 text-right font-medium">Líquido (Conta)</th>
                <th className="px-4 py-2 text-right font-medium">Imposto Pendente</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
                <th className="px-4 py-2 text-left font-medium">Conta</th>
                <th className="px-4 py-2 text-left font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((payout: any) => (
                <tr key={payout.id} className="border-t border-border hover:bg-subtle/50">
                  <td className="px-4 py-3 text-ink-secondary">
                    {formatDateBR(payout.arrivalDate)}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-emerald-600">
                    {formatCurrency(payout.grossAmount, payout.currency)}
                  </td>
                  <td className="px-4 py-3 text-right text-red-500">
                    {payout.feesAmount !== null &&
                    payout.feesAmount !== undefined &&
                    payout.feesAmount > 0
                      ? `- ${formatCurrency(payout.feesAmount, payout.currency)}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-right font-medium text-ink">
                    {formatCurrency(payout.amount, payout.currency)}
                  </td>
                  <td className="px-4 py-3 text-right text-amber-500">
                    {payout.taxEntry
                      ? `${formatCurrency(payout.taxEntry.amount, payout.currency)} (${(Number(payout.taxEntry.rate) * 100).toFixed(2)}%)`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={STATUS_TONE[payout.status] ?? 'neutral'}>
                      {STATUS_LABELS[payout.status] ?? payout.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-secondary">
                    {payout.bankAccount
                      ? `${payout.bankAccount.name} — ${payout.bankAccount.bankName}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {payout.status === 'PENDING' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => openConfirmModal(payout)}
                        >
                          <CheckCircle size={14} className="mr-1" /> Confirmar
                        </Button>
                      )}
                      {payout.status === 'PENDING' && (
                        <button
                          onClick={() => disputePayout.mutate({ id: payout.id, notes: undefined })}
                          className="text-ink-secondary hover:text-warning"
                          title="Contestar"
                        >
                          <AlertTriangle size={16} />
                        </button>
                      )}
                      {(payout.status === 'CONFIRMED' || payout.status === 'DISPUTED') && (
                        <button
                          onClick={() => {
                            const msg =
                              payout.status === 'CONFIRMED'
                                ? 'Reverter este repasse vai EXCLUIR as transações de receita e despesa criadas. Continuar?'
                                : 'Reverter este repasse para Pendente?';
                            if (window.confirm(msg)) {
                              revertPayout.mutate(payout.id);
                            }
                          }}
                          className="text-ink-secondary hover:text-ink-primary"
                          title="Reverter para Pendente"
                        >
                          <Undo2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={confirmModal}
        onClose={() => setConfirmModal(false)}
        title="Confirmar Repasse Stripe"
      >
        <div className="space-y-4">
          {confirmingPayout && (
            <div className="bg-subtle rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-ink-secondary">Data de chegada:</span>
                <span className="text-ink">{formatDateBR(confirmingPayout.arrivalDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-secondary">Receita bruta:</span>
                <span className="text-emerald-600 font-medium">
                  {formatCurrency(confirmingPayout.grossAmount, confirmingPayout.currency)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-secondary">Taxas Stripe:</span>
                <span className="text-red-500 font-medium">
                  - {formatCurrency(confirmingPayout.feesAmount, confirmingPayout.currency)}
                </span>
              </div>
              <div className="flex justify-between border-t border-border pt-1 mt-1">
                <span className="text-ink-secondary">Líquido (entra na conta):</span>
                <span className="text-ink font-semibold">
                  {formatCurrency(confirmingPayout.amount, confirmingPayout.currency)}
                </span>
              </div>
              {confirmCompanyId &&
                (() => {
                  const selectedCompany = companies.find((c: any) => c.id === confirmCompanyId);
                  const rate = getCompanyTaxRate(selectedCompany);
                  const gross = Number(confirmingPayout.grossAmount ?? confirmingPayout.amount);
                  const taxAmount = Math.round(gross * rate * 100) / 100;
                  if (rate === 0) {
                    return null;
                  }
                  return (
                    <div className="flex justify-between border-t border-border pt-1 mt-1">
                      <span className="text-ink-secondary">
                        Imposto pendente ({(rate * 100).toFixed(2)}%):
                      </span>
                      <span className="text-amber-500 font-medium">
                        {formatCurrency(taxAmount, confirmingPayout.currency)}
                      </span>
                    </div>
                  );
                })()}
            </div>
          )}
          <p className="text-xs text-ink-secondary">
            Ao confirmar, serão criadas como <strong>PENDENTES</strong>: uma transação de RECEITA
            (bruta), uma DESPESA (taxas Stripe) e uma DESPESA de IMPOSTO (mais o lançamento na aba
            Impostos). Confirme cada uma em <em>Transações</em> ou <em>Impostos</em> para
            efetivá-las no fluxo de caixa.
          </p>
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Empresa *
            </label>
            <select
              className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
              value={confirmCompanyId}
              onChange={(e) => {
                setConfirmCompanyId(e.target.value);
                setConfirmAccountId('');
              }}
            >
              <option value="">Selecione uma empresa...</option>
              {companies.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          {confirmCompanyId && (
            <div>
              <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
                Conta Corrente *
              </label>
              <select
                className="h-9 w-full border border-border rounded-md px-3 text-sm bg-surface text-ink"
                value={confirmAccountId}
                onChange={(e) => setConfirmAccountId(e.target.value)}
              >
                <option value="">Selecione uma conta...</option>
                {bankAccounts
                  .filter((a: any) => a.active)
                  .map((a: any) => (
                    <option key={a.id} value={a.id}>
                      {a.name} — {a.bankName}
                    </option>
                  ))}
              </select>
              {bankAccounts.length === 0 && (
                <p className="text-xs text-warning mt-1">
                  Nenhuma conta cadastrada para esta empresa. Cadastre em Configuração → Financeiro
                  → Contas Correntes.
                </p>
              )}
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
              Observações
            </label>
            <textarea
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-surface text-ink"
              rows={2}
              value={confirmNotes}
              onChange={(e) => setConfirmNotes(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setConfirmModal(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleConfirm}
              loading={confirmPayout.isPending}
              disabled={!confirmCompanyId || !confirmAccountId}
            >
              Confirmar e criar transações
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
